import React, { useState, useCallback, useEffect, useRef } from 'react';
import { 
  Volume2, Play, RefreshCw, Ear, Bell, Drum, Wind, 
  Music, Star, Heart, Flame, Sparkles, Binary, 
  Mic2, Radio, VolumeX 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ASSET_BASE_URL = "https://github.com/OmkarMore278/TalkyToon_Videos/releases/download/vs1";

const SOUND_OPTIONS = [
    { id: 'bell', name: "Bell", soundFile: `${ASSET_BASE_URL}/Bell.mp3`, icon: Bell, color: 'bg-blue-500', shadow: 'shadow-blue-200' },
    { id: 'drum', name: "Drum", soundFile: `${ASSET_BASE_URL}/Drum.mp3`, icon: Drum, color: 'bg-red-500', shadow: 'shadow-red-200' },
    { id: 'whistle', name: "Whistle", soundFile: `${ASSET_BASE_URL}/Whistle.mp3`, icon: Wind, color: 'bg-yellow-500', shadow: 'shadow-yellow-200' },
    { id: 'chime', name: "Chime", soundFile: `${ASSET_BASE_URL}/Chime.mp3`, icon: Music, color: 'bg-purple-500', shadow: 'shadow-purple-200' },
    { id: 'radio', name: "Radio", soundFile: `${ASSET_BASE_URL}/Radio.mp3`, icon: Radio, color: 'bg-emerald-500', shadow: 'shadow-emerald-200' },
    { id: 'mic', name: "Mic", soundFile: `${ASSET_BASE_URL}/Mic.mp3`, icon: Mic2, color: 'bg-orange-500', shadow: 'shadow-orange-200' },
    { id: 'echo', name: "Echo", soundFile: `${ASSET_BASE_URL}/echo.mp3`, icon: Binary, color: 'bg-pink-500', shadow: 'shadow-pink-200' },
    { id: 'mute', name: "Pop", soundFile: `${ASSET_BASE_URL}/pop.mp3`, icon: VolumeX, color: 'bg-cyan-500', shadow: 'shadow-cyan-200' },
];

const EchoQuestGame = () => {
    const [sequence, setSequence] = useState([]);
    const [playerSequence, setPlayerSequence] = useState([]);
    const [level, setLevel] = useState(0);
    const [streak, setStreak] = useState(0);
    const [statusMessage, setStatusMessage] = useState("Ready for a Quest?");
    const [isPlayerTurn, setIsPlayerTurn] = useState(false);
    const [isLocked, setIsLocked] = useState(false);
    const [activeId, setActiveId] = useState(null);
    
    // Persistent audio reference to avoid memory leaks
    const audioRef = useRef(null);

    // Audio Playback logic similar to Whisper Woods
// Robust Playback Engine with 3-second limit
const playSound = useCallback((src) => {
    if (!audioRef.current) audioRef.current = new Audio();
    
    // Reset previous audio if still playing
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    
    audioRef.current.src = src;
    audioRef.current.play().catch(err => console.log("Audio play blocked", err));

    // Stop playback after 3 seconds
    const stopTimer = setTimeout(() => {
        if (audioRef.current && audioRef.current.src.includes(src)) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
    }, 3000);

    return () => clearTimeout(stopTimer);
}, []);

    const startNewRound = useCallback((currentSequence, currentLevel) => {
        setIsLocked(true);
        setIsPlayerTurn(false);
        setPlayerSequence([]);
        
        // Logic: Gradually add more possible sounds as level increases
        const poolSize = Math.min(4 + Math.floor(currentLevel / 3), 8);
        const availableSounds = SOUND_OPTIONS.slice(0, poolSize);
        const nextSound = availableSounds[Math.floor(Math.random() * availableSounds.length)].id;
        
        const newSequence = [...currentSequence, nextSound];
        setSequence(newSequence);
        setStatusMessage("Watch the Pattern...");

        // Play sequence with timing
        const delay = Math.max(800 - (currentLevel * 40), 500); 
        
        newSequence.forEach((id, index) => {
            setTimeout(() => {
                const soundData = SOUND_OPTIONS.find(s => s.id === id);
                setActiveId(id);
                playSound(soundData.soundFile);
                
                // Visual feedback duration
                setTimeout(() => setActiveId(null), delay - 150);
            }, delay * (index + 1));
        });

        // Hand over control to player
        setTimeout(() => {
            setIsLocked(false);
            setIsPlayerTurn(true);
            setStatusMessage("Your Turn! Repeat!");
        }, delay * (newSequence.length + 1));
    }, [playSound]);

    const handleStartGame = () => {
        setLevel(1);
        setStreak(0);
        setStatusMessage("Focus...");
        // Start from empty sequence
        setTimeout(() => startNewRound([], 1), 600);
    };

    const handlePlayerClick = (id) => {
        if (!isPlayerTurn || isLocked) return;

        const sound = SOUND_OPTIONS.find(s => s.id === id);
        playSound(sound.soundFile);
        setActiveId(id);
        setTimeout(() => setActiveId(null), 300);

        const newPlayerSequence = [...playerSequence, id];
        setPlayerSequence(newPlayerSequence);

        const currentStep = newPlayerSequence.length - 1;

        // Check if correct
        if (newPlayerSequence[currentStep] !== sequence[currentStep]) {
            setStatusMessage(`❌ Wrong Pattern!`);
            setIsLocked(true);
            setTimeout(() => {
                setLevel(0);
                setStreak(0);
                setIsPlayerTurn(false);
                setIsLocked(false);
                setStatusMessage("Try Again?");
            }, 1500);
            return;
        }

        // Check if sequence completed
        if (newPlayerSequence.length === sequence.length) {
            setIsPlayerTurn(false);
            setStreak(prev => prev + 1);
            setStatusMessage(`🌟 Perfect!`);
            
            setTimeout(() => {
                const nextLevel = level + 1;
                setLevel(nextLevel);
                startNewRound(sequence, nextLevel);
            }, 1200);
        }
    };

    return (
        <div className="min-h-screen bg-[#F0F4FF] p-4 md:p-8 font-['Fredoka',_sans-serif]">
            <style jsx global>{`
                @import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@400;600;700&display=swap');
            `}</style>

            <main className="max-w-4xl mx-auto">
                {/* Header Stats */}
                <div className="flex justify-between items-center mb-6 bg-white p-4 rounded-3xl shadow-sm border border-indigo-50">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-indigo-600 rounded-2xl shadow-lg">
                            <Ear className="text-white w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-xl md:text-2xl font-black text-slate-800 leading-none">EchoQuest</h1>
                            <p className="text-indigo-400 text-[10px] font-bold uppercase tracking-wider">Memory Master</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="hidden sm:flex flex-col items-end mr-2">
                            <span className="text-[10px] font-bold text-slate-400 uppercase leading-none mb-1">Streak</span>
                            <div className="flex items-center gap-1">
                                <Flame className="w-4 h-4 text-orange-500 fill-orange-500" />
                                <span className="font-black text-slate-700">{streak}</span>
                            </div>
                        </div>
                        <div className="bg-indigo-600 px-4 py-2 rounded-2xl text-white flex items-center gap-2 shadow-md">
                            <Star className="w-4 h-4 fill-yellow-300 text-yellow-300" />
                            <span className="font-bold">LVL {level}</span>
                        </div>
                    </div>
                </div>

                {/* Game Area */}
                <div className="bg-white rounded-[3rem] p-6 md:p-12 shadow-2xl border-4 border-white relative overflow-hidden">
                    
                    {/* Progress indicator */}
                    {level > 0 && (
                        <div className="absolute top-0 left-0 w-full h-1.5 bg-slate-100">
                            <motion.div 
                                className="h-full bg-indigo-500"
                                initial={{ width: 0 }}
                                animate={{ width: `${(playerSequence.length / sequence.length) * 100}%` }}
                            />
                        </div>
                    )}

                    <div className="text-center mb-10 h-10 flex items-center justify-center">
                        <AnimatePresence mode="wait">
                            <motion.h2
                                key={statusMessage}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className={`text-xl md:text-2xl font-black tracking-wide ${
                                    statusMessage.includes('🌟') ? 'text-green-500' : 
                                    statusMessage.includes('❌') ? 'text-red-500' : 'text-indigo-600'
                                }`}
                            >
                                {statusMessage}
                            </motion.h2>
                        </AnimatePresence>
                    </div>

                    {/* Responsive Grid - Logic: Limit items shown based on level difficulty */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-12">
                        {SOUND_OPTIONS.slice(0, level === 0 ? 4 : Math.min(4 + Math.floor(level / 3), 8)).map((option) => {
                            const Icon = option.icon;
                            const isActive = activeId === option.id;
                            
                            return (
                                <button
                                    key={option.id}
                                    onClick={() => handlePlayerClick(option.id)}
                                    disabled={!isPlayerTurn || isLocked}
                                    className={`relative h-28 md:h-36 rounded-[2rem] border-b-8 active:border-b-0 active:translate-y-1 transition-all flex flex-col items-center justify-center gap-2
                                        ${option.color} ${option.shadow} border-black/10
                                        ${isActive ? 'scale-105 brightness-125 translate-y-1 border-b-0 ring-4 ring-white shadow-none' : 'hover:-translate-y-1'}
                                        disabled:cursor-default disabled:opacity-80
                                    `}
                                >
                                    <Icon className={`w-10 h-10 text-white ${isActive ? 'animate-bounce' : ''}`} />
                                    <span className="text-white font-black text-[10px] md:text-xs uppercase opacity-90">
                                        {option.name}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex justify-center">
                        <button 
                            onClick={handleStartGame} 
                            disabled={isLocked && level > 0}
                            className="group flex items-center gap-4 px-10 py-4 rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-black text-xl shadow-2xl transition-all active:scale-95 disabled:opacity-50"
                        >
                            {level > 0 ? <RefreshCw className="w-6 h-6" /> : <Play className="w-6 h-6 fill-white" />}
                            {level > 0 ? 'RESET QUEST' : 'START QUEST'}
                        </button>
                    </div>
                </div>

                {/* Instruction Cards */}
                <div className="mt-8 grid grid-cols-3 gap-4">
                    {[
                        { icon: Volume2, text: "Listen" },
                        { icon: Sparkles, text: "Watch" },
                        { icon: Heart, text: "Repeat" }
                    ].map((step, i) => (
                        <div key={i} className="p-4 bg-white rounded-2xl border border-indigo-50 text-center shadow-sm">
                            <step.icon className="w-5 h-5 mx-auto mb-2 text-indigo-400" />
                            <p className="text-[10px] font-bold text-slate-500 uppercase">{step.text}</p>
                        </div>
                    ))}
                </div>
            </main>
        </div>
    );
};

export default EchoQuestGame;